"use client"

import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogTitle, DialogTrigger } from "../ui/dialog";
import { Button } from "../ui/button";
import { CreateStatus } from "@/constants/status";
import Dropdown, { dropdownCss, DropdownInterface } from "../ui/dropdown";
import z from "zod";
import { DomainSchema } from "@/schemas/domain.schema";
import { getDomainById, getNormalDomains } from "@/services/domains.service";
import { Label } from "../ui/label";
import { cn } from "@/lib/utils";
import { createCronjobEntry, getCronjobEntry } from "@/services/cronjob.service";
import { compareVersions } from 'compare-versions';

interface Props {
    domainid: string | null,
    cronjoboptions: Array<string>,
    updatedata: () => void,
}

export default function CronjobCreateModal(props: Props){
    const [open, setOpen] = useState(false);
    const [creationStatus, setCreationStatus] = useState(CreateStatus.standBy);
    const [failedMessage, setFailedMessage] = useState("");
    const [domains, setDomains] = useState<DropdownInterface[]>([]);
    const [domain, setDomain] = useState<z.infer<typeof DomainSchema>>();
    const [cronjobDropdownOptions, setCronjobDropdownOptions] = useState<DropdownInterface[]>([]);

    const domainRef = useRef("");
    const cronjobOptionsRef = useRef("");

    async function getDomain(domainID: string){
        var result = await getDomainById(domainID);
        setDomain(result);
        cronjobOptions(result.ID);
        domainRef.current = result.ID;
    }
    async function getDomains(){
        var domains: DropdownInterface[] = [];
        try {
            var result = await getNormalDomains();
            domains = Object.keys(result).map(function (domainIndex) {
                const domain = result[Number(domainIndex)];
                return {id: Number(domain.ID), value: domain.ID, text: domain.Name};
            });
        } catch (error) {
            console.log("----------------- Error Occurred - getCronjobDomains -----------------");
            console.log(error);
        }
        setDomains(domains);
        cronjobOptions(domains[0].value.toString());
        domainRef.current = domains[0].value.toString();
    }

    async function cronjobOptions(domainID: string) {
        var options: DropdownInterface[] = [];
        try {
            var result = await getCronjobEntry(domainID);
            if(Object.keys(result).length > 0){
                if(result.CRM_Sync == null || !result.CRM_Sync){
                    options.push({id: 1, value: "CRM_Sync", text: "CRM-Sync"});
                }
                if(compareVersions(result.Version.toString(), "1.5") >= 0){
                    if(result.Rekvisition == null || !result.Rekvisition){
                        options.push({id: 2, value: "Rekvisition", text: "Requisition"});
                    }
                    if(result.UploadsCleanup == null || !result.UploadsCleanup){
                        options.push({id: 3, value: "UploadsCleanup", text: "Uploads Cleanup"});
                    }
                }

                if(compareVersions(result.Version.toString(), "1.5.2") >= 0){
                    if(result.MailMergeService == null || !result.MailMergeService){
                        options.push({id: 2, value: "MailMergeService", text: "Mail Merge Service"});
                    }
                }
            }
        } catch (error) {
            console.log("----------------- Error Occurred - getCronjobEntry -----------------");
            console.log(error);
        }
        setCronjobDropdownOptions(options);
        if(options.length > 0){
            cronjobOptionsRef.current = options[0].value.toString();
        }
    }

    async function createCronjob(){
        const formData = new FormData();
        formData.append("domainID", domainRef.current);
        formData.append("cronType", cronjobOptionsRef.current);
        formData.append("enable", "true");

        var result = null;
        try {
            result = await createCronjobEntry(formData);
        }  catch (error) {
            setCreationStatus(CreateStatus.failed);
            errorHandler(error);
        }

        if(result){
            setCreationStatus(CreateStatus.completed);
            props.updatedata();
            if(props.domainid != null){
                getDomain(props.domainid);
            } else {
                getDomains();
            }
            setTimeout(() => {
                setOpen(false);
                setCreationStatus(CreateStatus.standBy);
            }, 2000);
            return;
        }

        console.log(result)
        setCreationStatus(CreateStatus.failed);
        return;
    }

    function errorHandler(data: any){
        console.log("Erorr occoured");
        console.log(data);
        setFailedMessage("An error occurred while creating the entry. Check console.log for more information");
    }

    useEffect(() => {
        if(props.domainid != null){
            getDomain(props.domainid);
        } else {
            getDomains();
        }
    }, []);

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button className="cursor-pointer">Create Cronjob Entry</Button>
            </DialogTrigger>
            <DialogContent className="max-w-sm">
                <DialogTitle>Create Cronjob Entry</DialogTitle>
                <DialogDescription> 
                </DialogDescription>
                <div>
                    {(creationStatus === CreateStatus.standBy ||
                        creationStatus === CreateStatus.notAuthroized) && (
                            <div>
                                <div className="col-start-1 col-end-4 rounded-md border-black">
                                    {domains.length > 0 && (
                                        <>
                                            <Label
                                                data-slot="form-label"
                                                className={cn("data-[error=true]:text-destructive")}
                                                htmlFor={"domainid"}
                                                {...props} />
                                            <Dropdown
                                                id="domainid"
                                                title="domainid"
                                                className={dropdownCss}
                                                onChange={(d) => {
                                                    cronjobOptions(d.target.value);
                                                    domainRef.current = d.target.value;
                                                }}
                                            >
                                                {domains}
                                            </Dropdown>
                                        </>
                                    )}
                                    {domain != undefined && (
                                        <p>Creating Cronjob for: {domain.Name}</p>
                                    )}
                                    {cronjobDropdownOptions.length > 0 && (
                                        <>
                                            <Label
                                                data-slot="form-label"
                                                className={cn("data-[error=true]:text-destructive")}
                                                htmlFor={"cronjobcreateoption"}
                                                {...props} />
                                            <Dropdown
                                                id="cronjobcreateoption"
                                                title="cronjobcreateoption"
                                                className={dropdownCss}
                                                onChange={(d) => {
                                                    // cronjobOptions(d.target.value);
                                                    cronjobOptionsRef.current = d.target.value;
                                                }}
                                            >
                                                {cronjobDropdownOptions}
                                            </Dropdown>
                                        </>
                                    )}
                                    {cronjobDropdownOptions.length <= 0 && (
                                        <p>No Cronjob possibilities for this domain</p>
                                    )}
                                </div>
                                <div className="w-full">
                                    {creationStatus == CreateStatus.notAuthroized && (
                                    <h1 className="w-full text-xl font-bold text-black dark:text-white mb-2 py-1 px-2 rounded-md bg-red-500">
                                        Authorization to create failed
                                    </h1>
                                    )}
                                    {cronjobDropdownOptions.length > 0 && (
                                        <Button
                                            className="w-[300px] mt-4 cursor-pointer float-right"
                                            type="button"
                                            onClick={async () => {
                                                await createCronjob();
                                            }}
                                        >
                                            Create Entry
                                        </Button>
                                    )}
                                </div>
                            </div>
                    )}
                </div>
                <DialogFooter>
                    <div className="grid grid-cols-6 gap-4">
                        <div className="col-start-1 col-end-7 rounded-md border-black">
                            {creationStatus == CreateStatus.onGoing && (
                                <div className="flex flex-col w-full max-w-2xl  gap-y-4">
                                    <p>Creating Entry...</p>
                                </div>
                            )}
                            {creationStatus == CreateStatus.completed && (
                                <div className="flex flex-col w-full max-w-2xl  gap-y-4">
                                    <p>Created Entry! The modal will close momentarily</p>
                                </div>
                            )}
                            {creationStatus == CreateStatus.failed && (
                                <div className="flex flex-col w-full max-w-2xl  gap-y-4">
                                    <p>{failedMessage}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}